import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, ArrowLeft, Loader2, PartyPopper, ShieldCheck, Smartphone, Timer, UserRound, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Switch } from "@/components/ui/switch";
import PageHeader from "@/components/PageHeader";
import FastPostCard from "@/components/FastPostCard";
import FastPostAccountPicker, { filterByAccount, getAccountPref, setAccountPref } from "@/components/FastPostAccountPicker";
import HelperBar from "@/components/HelperBar";
import { isMobile } from "@/lib/pwa";
import api, { copyText, errMsg } from "@/lib/api";
import { closeGroupWindow, getPopupPref, openGroupWindow, setPopupPref } from "@/lib/popup";
import { gapSeconds, getAutoNext, getAutoPost, getGapPref, hasHelper, onHelperEvent, openWithHelper, setAutoNext as saveAutoNext, setAutoPost as saveAutoPost, setGapPref } from "@/lib/helper";

const fmtSecs = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
const SKIP_DELAY = 10; // seconds before moving on after a failed group

function Finished({ done, skipped, failed = [], report }) {
  const any = done + skipped + failed.length > 0;
  return (
    <div className="panel fade-up p-10 text-center" data-testid="fast-post-finished">
      <PartyPopper className="mx-auto h-10 w-10 text-emerald-400" />
      <h2 className="mt-4 text-2xl font-bold">{any ? "Semua group selesai!" : "Tiada group perlu dipost sekarang"}</h2>
      {any && <p className="mt-2 text-sm text-muted-foreground" data-testid="fast-post-summary">{done} dipost · {skipped} dilangkau · {failed.length} gagal</p>}
      {failed.length > 0 && (
        <div className="mx-auto mt-6 max-w-xl rounded-lg border border-red-500/30 bg-red-500/5 p-4 text-left" data-testid="fast-post-failed-report">
          <p className="mb-3 flex items-center gap-2 text-sm font-semibold text-red-200"><AlertTriangle className="h-4 w-4" /> Laporan group gagal post</p>
          <ul className="space-y-2">
            {failed.map((f) => (
              <li key={f.tid} className="flex items-start justify-between gap-3 text-sm" data-testid={`fast-post-failed-${f.tid}`}>
                <div className="min-w-0">
                  <p className="truncate font-medium text-slate-200">{f.group_name}</p>
                  <p className="text-xs text-red-300">{f.reason}</p>
                </div>
                <a href={f.group_url} target="_blank" rel="noopener noreferrer" className="shrink-0 text-xs text-primary hover:underline">Buka group</a>
              </li>
            ))}
          </ul>
          <p className="mt-3 text-xs text-muted-foreground">Group gagal ditanda "Gagal" dalam Sejarah. Tekan "Buat Asal" di Sejarah untuk cuba semula.</p>
        </div>
      )}
      {any && report && <p className="mt-4 text-xs text-muted-foreground" data-testid="fast-post-report-status">{report === "sending" ? "Menghantar laporan..." : report === "error" ? "Laporan gagal dihantar." : report === "emailed" ? "Laporan dihantar ke loceng notifikasi & emel anda." : "Laporan dihantar ke loceng notifikasi."}</p>}
      <div className="mt-6 flex justify-center gap-2">
        <Button asChild><Link to="/jadual">Kembali ke Giliran</Link></Button>
        <Button asChild variant="secondary"><Link to="/tulis">Tulis post baharu</Link></Button>
      </div>
    </div>
  );
}

export default function FastPost() {
  const [items, setItems] = useState(null);
  const [allItems, setAllItems] = useState([]);
  const [account, setAccount] = useState(getAccountPref);
  const [notes, setNotes] = useState({});
  const [index, setIndex] = useState(0);
  const [stats, setStats] = useState({ done: 0, skipped: 0 });
  const [failed, setFailed] = useState([]);
  const [report, setReport] = useState(null);
  const [watchdog, setWatchdog] = useState(0); // deadline (ms) for a helper response
  const [busy, setBusy] = useState(false);
  const [usePopup, setUsePopup] = useState(getPopupPref);
  const [helper] = useState(() => hasHelper() && !isMobile());
  const [mobile] = useState(isMobile);
  const [autoNext, setAutoNextState] = useState(getAutoNext);
  const [autoPost, setAutoPostState] = useState(() => hasHelper() && getAutoPost());
  const [gap, setGapState] = useState(getGapPref);
  const changeGap = (v) => { setGapState(v); setGapPref(v); };
  // Auto-advance timer is deadline based so it stays accurate even if the browser throttles this tab.
  const [deadline, setDeadline] = useState(0);
  const [now, setNow] = useState(Date.now());
  const countdown = deadline ? Math.max(0, Math.ceil((deadline - now) / 1000)) : 0;
  const setCountdown = (secs) => { setNow(Date.now()); setDeadline(secs ? Date.now() + secs * 1000 : 0); };

  const toggleAutoNext = (on) => { setAutoNextState(on); saveAutoNext(on); if (!on) setCountdown(0); };
  const toggleAutoPost = (on) => {
    setAutoPostState(on); saveAutoPost(on);
    if (on) { setAutoNextState(true); saveAutoNext(true); toast.success("Auto tekan Siaran dihidupkan. Tekan butang 1 untuk mula."); } else setCountdown(0);
  };
  const togglePopup = (on) => { setUsePopup(on); setPopupPref(on); if (!on) closeGroupWindow(); };
  useEffect(() => {
    Promise.all([api.get("/queue", { params: { bucket: "due" } }), api.get("/groups")])
      .then(([q, g]) => {
        const map = Object.fromEntries(g.data.map((x) => [x.id, x]));
        const groupItems = q.data.filter((i) => i.kind === "group");
        const acc = account === "all" || filterByAccount(groupItems, map, account).length ? account : "all";
        setNotes(map);
        setAllItems(groupItems);
        setAccount(acc);
        setItems(filterByAccount(groupItems, map, acc));
      })
      .catch(() => setItems([]));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const changeAccount = (v) => { setAccount(v); setAccountPref(v); setItems(filterByAccount(allItems, notes, v)); };

  const mark = async (status, reason, via = "manual") => {
    const item = items[index];
    if (!item) return false;
    setBusy(true);
    try {
      await api.post(`/posts/${item.post_id}/targets/${item.tid}/status`, status === "failed" ? { status, error: reason, via } : { status, via });
      if (status === "failed") setFailed((f) => [...f, { tid: item.tid, group_name: item.group_name, group_url: item.group_url, reason }]);
      else setStats((s) => ({ ...s, [status === "done" ? "done" : "skipped"]: s[status === "done" ? "done" : "skipped"] + 1 }));
      setIndex((i) => i + 1);
      return true;
    } catch (e) { toast.error(errMsg(e)); return false; } finally { setBusy(false); }
  };

  const total = items?.length || 0;
  const current = items?.[index];
  const nextItem = items?.[index + 1];
  const [openedTid, setOpenedTid] = useState(null);

  // Open a group: via the Chrome helper (auto-fill "Cipta siaran") or a plain popup/tab.
  const openItem = useCallback((item, text) => {
    copyText(text);
    setOpenedTid(item.tid);
    if (helper) {
      openWithHelper(item, text, { autoPost });
      setWatchdog(Date.now() + 90000);
      toast.success(autoPost ? `Membuka ${item.group_name} - teks diisi & Siaran ditekan automatik.` : `Membuka ${item.group_name} - teks akan diisi automatik. Anda cuma tekan Siaran.`);
      return;
    }
    const mode = openGroupWindow(item.group_url, usePopup);
    if (mode === "blocked") toast.error("Popup disekat oleh pelayar. Benarkan popup untuk laman ini.");
    else toast.success(`Teks disalin & ${item.group_name} dibuka. Tampal (Ctrl+V) dan post.`);
  }, [helper, usePopup, autoPost]);

  const doneNext = () => {
    if (!nextItem || busy) return;
    setCountdown(0);
    openItem(nextItem, nextItem.text);
    mark("done");
  };

  // Events from the Chrome helper (filled / failed / user pressed "Siaran").
  const live = useRef({});
  live.current = { current, nextItem, mark, autoNext, autoPost, gap };

  // Helper could not post here -> mark "Gagal" with reason, skip, and (in auto mode) carry on.
  const skipFailed = useCallback(async (reason) => {
    const { current: cur, nextItem: nxt, mark: doMark, autoNext: auto, autoPost: autoP } = live.current;
    if (!cur) return;
    setWatchdog(0);
    const ok = await doMark("failed", reason, autoP ? "helper_auto" : "helper");
    if (!ok) return;
    toast.error(`${cur.group_name}: ${reason}. Dilangkau.`, { duration: 8000 });
    if ((auto || autoP) && nxt) setCountdown(SKIP_DELAY);
  }, []);

  useEffect(() => {
    if (!helper) return undefined;
    return onHelperEvent(async (ev) => {
      const { current: cur, nextItem: nxt, mark: doMark, autoNext: auto, autoPost: autoP, gap: gapPref } = live.current;
      if (ev.type === "CLOSED") { setDeadline(0); setWatchdog(0); if (autoP) toast.info("Popup ditutup - auto dihentikan."); return; }
      if (!cur || ev.jobId !== cur.tid) return;
      if (ev.type === "FILLED") {
        setWatchdog(Date.now() + 90000);
        toast.success(autoP ? "Teks diisi. Helper akan tekan Siaran sebentar lagi..." : "Teks sudah diisi dalam Cipta siaran. Semak & tekan Siaran.");
      }
      if (ev.type === "FAILED") {
        setDeadline(0);
        if (ev.code === "login") {
          setWatchdog(0);
          toast.error("Belum log masuk Facebook dalam popup. Log masuk dahulu - auto dihentikan.", { duration: 10000 });
          return;
        }
        if (ev.code === "skip" || autoP || auto) { skipFailed(ev.error || "Gagal dipost"); return; }
        setWatchdog(0);
        toast.error(`Helper: ${ev.error || "gagal mengisi"}. Tampal Ctrl+V secara manual (teks sudah disalin).`, { duration: 10000 });
      }
      if (ev.type === "POSTED") {
        setWatchdog(0);
        const ok = await doMark("done", undefined, autoP ? "helper_auto" : "helper");
        if (ok) toast.success(`${cur.group_name} ditanda sudah post`);
        if (ok && (auto || autoP) && nxt) setCountdown(gapSeconds(gapPref));
      }
    });
  }, [helper, skipFailed]);

  // Watchdog: no response from the popup in time (only in auto modes) -> treat as failed & skip.
  useEffect(() => {
    if (!watchdog) return undefined;
    const t = setTimeout(() => {
      setWatchdog(0);
      if (live.current.autoPost || live.current.autoNext) skipFailed("Tiada respons dari popup (masa tamat)");
    }, Math.max(0, watchdog - Date.now()));
    return () => clearTimeout(t);
  }, [watchdog, skipFailed]);

  // End of session -> send report (in-app notification + email if enabled) once.
  const reported = useRef(false);
  useEffect(() => {
    if (!items || current || reported.current) return;
    if (stats.done + stats.skipped + failed.length === 0) return;
    reported.current = true;
    setReport("sending");
    api.post("/fast-post/report", { done: stats.done, skipped: stats.skipped, failed: failed.map(({ group_name, group_url, reason }) => ({ group_name, group_url, reason })) })
      .then((r) => setReport(r.data.emailed ? "emailed" : "sent"))
      .catch(() => setReport("error"));
  }, [items, current, stats, failed]);

  // Auto-advance countdown -> open the (now current) next group via helper.
  useEffect(() => {
    if (!deadline) return undefined;
    if (!current) { setDeadline(0); return undefined; }
    if (now >= deadline) { setDeadline(0); openItem(current, current.text); return undefined; }
    const t = setTimeout(() => setNow(Date.now()), 1000);
    return () => clearTimeout(t);
  }, [deadline, now, current, openItem]);

  useEffect(() => { if (items && !current) closeGroupWindow(); }, [items, current]);

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader eyebrow="Mod Post Pantas" title="Post satu demi satu" desc="Anda sendiri yang tekan Post di Facebook. Selamat untuk akaun anda, tapi jauh lebih pantas."
        action={<Button asChild variant="ghost" data-testid="fast-post-back-btn"><Link to="/jadual"><ArrowLeft className="mr-2 h-4 w-4" /> Giliran</Link></Button>} />
      {items === null ? <Loader2 className="h-5 w-5 animate-spin text-primary" /> : (
        <>
          <FastPostAccountPicker items={allItems} groups={notes} value={account} onChange={changeAccount} disabled={index > 0} />
          {current && notes[current.group_id]?.fb_account && (
            <div className={`mb-4 flex items-center gap-2 rounded-lg border px-4 py-2 text-sm ${index > 0 && notes[items[index - 1]?.group_id]?.fb_account !== notes[current.group_id]?.fb_account ? "border-amber-500/40 bg-amber-500/10 text-amber-100" : "border-border bg-card/60 text-muted-foreground"}`} data-testid="fast-post-current-account">
              <UserRound className="h-4 w-4 shrink-0" />
              {index > 0 && notes[items[index - 1]?.group_id]?.fb_account !== notes[current.group_id]?.fb_account ? "Tukar akaun Facebook dahulu ke: " : "Akaun Facebook: "}
              <b className="text-slate-100">{notes[current.group_id].fb_account}</b>
            </div>
          )}
          {total > 0 && (
            <div className="mb-6" data-testid="fast-post-progress">
              <div className="mb-2 flex justify-between font-mono text-xs text-muted-foreground">
                <span>Group {Math.min(index + 1, total)} / {total}</span>
                <span>{stats.done} dipost · {stats.skipped} dilangkau{failed.length ? ` · ${failed.length} gagal` : ""}</span>
              </div>
              <Progress value={(index / total) * 100} className="h-2" />
            </div>
          )}
          {current && (mobile ? (
            <div className="mb-4 flex items-start gap-3 rounded-lg border border-primary/30 bg-primary/10 px-4 py-3" data-testid="fast-post-mobile-tip">
              <Smartphone className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
              <div className="text-xs leading-relaxed text-muted-foreground">
                <p className="text-sm font-medium text-slate-100">Mod telefon</p>
                Tekan <b className="text-slate-200">Salin & Buka Group</b>, app Facebook akan dibuka. Tekan lama pada kotak post, pilih <b className="text-slate-200">Tampal</b>, kemudian <b className="text-slate-200">Siaran</b>. Kembali ke PostPintar dan jawab "Sudah post?".
              </div>
            </div>
          ) : <HelperBar autoNext={autoNext} setAutoNext={toggleAutoNext} autoPost={autoPost} setAutoPost={toggleAutoPost} gap={gap} setGap={changeGap} />)}
          {countdown > 0 && current && (
            <div className="mb-4 flex items-center justify-between gap-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-100" data-testid="fast-post-countdown">
              <span className="flex items-center gap-2"><Timer className="h-4 w-4 shrink-0" /> <span>{autoPost ? "Auto Siaran" : "Membuka"} <b>{current.group_name}</b> dalam <span className="font-mono" data-testid="fast-post-countdown-time">{fmtSecs(countdown)}</span></span></span>
              <Button size="sm" variant="ghost" onClick={() => setCountdown(0)} className="text-amber-100" data-testid="fast-post-countdown-cancel"><X className="mr-1 h-4 w-4" /> Batal</Button>
            </div>
          )}
          {current && !helper && !mobile && (
            <label className="mb-4 flex items-center justify-between gap-3 rounded-lg border border-border bg-card/60 px-4 py-3" data-testid="fast-post-popup-toggle-row">
              <div>
                <p className="text-sm font-medium">Buka dalam popup</p>
                <p className="text-xs text-muted-foreground">Group dibuka dalam tetingkap kecil di sebelah kanan. Selepas post, tutup dengan Ctrl+W.</p>
              </div>
              <Switch checked={usePopup} onCheckedChange={togglePopup} data-testid="fast-post-popup-toggle" />
            </label>
          )}
          {current ? (
            <FastPostCard item={current} usePopup={usePopup && !mobile} mobile={mobile} helper={helper} onOpen={(text) => { setCountdown(0); openItem(current, text); }} opened={openedTid === current.tid} onDoneNext={nextItem ? doneNext : undefined} notes={notes[current.group_id]?.notes} needsApproval={notes[current.group_id]?.needs_approval} busy={busy} onDone={() => { setCountdown(0); mark("done"); }} onSkip={() => { setCountdown(0); mark("skipped"); }} />
          ) : (
            <Finished done={stats.done} skipped={stats.skipped} failed={failed} report={report} />
          )}
          <p className="mt-6 flex items-start gap-2 text-xs leading-relaxed text-muted-foreground">
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" />
            Tip: rehat beberapa minit antara group dan guna teks berbeza (variasi AI) untuk elak Facebook tandakan sebagai spam.
          </p>
        </>
      )}
    </div>
  );
}
